import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Send, Loader2, Bot, CheckCircle2, XCircle, Clock, Target, ShieldAlert, ArrowRightLeft, Sparkles, Mic, Square, Volume2, VolumeX, Play, LogOut, TrendingUp, X, Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { speech } from "@/lib/speech";
import { useAuth } from "@/context/AuthContext";

const tz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export const BrokerChat = ({ sessionId, initialMessages = null, onSessionUpdate, refreshKey = 0, autoSpeak = true, className = "" }) => {
  const { user } = useAuth();
  const lang = speech.langFor(user?.language || "en");
  const [messages, setMessages] = useState(initialMessages || []);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [skipFor, setSkipFor] = useState(null); // message_id awaiting reason
  const bottomRef = useRef(null);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("broker_voice") === "1");
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const recorderRef = useRef(null);

  useEffect(() => speech.subscribe(({ speaking: s, current }) => setSpeakingId(s ? current : null)), []);

  // Single speech engine (lib/speech): cancels overlapping speech and dedupes by message id.
  const speak = (m) => { if (m?.content) speech.speak(m.content, { lang, key: m.message_id, urgent: true, force: true }); };
  const stopSpeaking = () => speech.cancel();
  const toggleVoice = () => setVoiceOn((v) => { localStorage.setItem("broker_voice", v ? "0" : "1"); if (v) stopSpeaking(); return !v; });

  useEffect(() => {
    if (!voiceOn || !autoSpeak || !messages.length) return;
    const last = messages[messages.length - 1];
    if (last.role === "assistant" && !last.message_id.startsWith("tmp_") && !speech.wasSpoken(last.message_id)) {
      const isFresh = Date.now() - new Date(last.created_at).getTime() < 120000;
      if (isFresh) speech.speak(last.content, { lang, key: last.message_id });
      else speech.markSpoken(last.message_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, voiceOn, autoSpeak]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        if (blob.size < 1000) { toast.error("Recording too short"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice.webm");
          const { data } = await api.post("/broker/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          if (data.text?.trim()) { setText(data.text.trim()); setTimeout(() => sendText(data.text.trim()), 50); }
          else toast.error("Couldn't hear anything — try again");
        } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Transcription failed"); }
        finally { setTranscribing(false); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      stopSpeaking();
    } catch { toast.error("Microphone blocked or unavailable"); }
  };
  const stopRecording = () => { recorderRef.current?.stop(); setRecording(false); };

  const tradeAction = async (m, action, newStop) => {
    try {
      const { data } = await api.post(`/broker/sessions/${sessionId}/trade-action`, { trade_id: m.followup.trade_id, action, new_stop: newStop ?? null });
      setMessages((cur) => [...cur.map((x) => (x.message_id === m.message_id ? { ...x, followup: { ...x.followup, actioned: action } } : x)), data.event]);
      toast.success(action === "exit" ? `Trade exited · P&L ${data.trade.pnl >= 0 ? "+" : ""}${data.trade.pnl}` : `Trailing stop set at ${data.trade.broker_tracking.stop}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!sessionId) return;
    if (initialMessages) { setMessages(initialMessages); return; }
    api.get(`/broker/sessions/${sessionId}/messages`).then(({ data }) => setMessages(data.messages)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !refreshKey) return;
    api.get(`/broker/sessions/${sessionId}/messages`).then(({ data }) => setMessages(data.messages)).catch(() => {});
  }, [sessionId, refreshKey]);

  useEffect(() => {
    if (!sessionId) return;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/broker/sessions/${sessionId}/messages`);
        setMessages((cur) => (data.messages.length !== cur.length ? data.messages : cur));
      } catch { /* ignore */ }
    }, 45000);
    return () => clearInterval(id);
  }, [sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, sending]);

  const send = async (e) => { e?.preventDefault(); await sendText(text.trim()); };

  const sendText = async (t) => {
    if (!t || sending) return;
    setText("");
    if (skipFor) { await submitSkipReason(skipFor, t); return; }
    setSending(true);
    setMessages((m) => [...m, { message_id: `tmp_${Date.now()}`, role: "user", content: t, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post(`/broker/sessions/${sessionId}/messages`, { text: t, tz: tz() });
      setMessages((m) => [...m.filter((x) => !x.message_id.startsWith("tmp_")), data.user_message, data.reply]);
      onSessionUpdate?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setMessages((m) => m.filter((x) => !x.message_id.startsWith("tmp_")));
    } finally { setSending(false); }
  };

  const confirm = async (msg) => {
    try {
      const { data } = await api.post(`/broker/sessions/${sessionId}/confirm`, { message_id: msg.message_id, quantity: 1 });
      setMessages((m) => [...m.map((x) => (x.message_id === msg.message_id ? { ...x, decision: "traded", trade_id: data.trade.trade_id } : x)), data.event]);
      toast.success("Trade logged in your journal — I'll follow up when target or stop hits");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const skip = async (msg) => {
    try {
      const { data } = await api.post(`/broker/sessions/${sessionId}/skip`, { message_id: msg.message_id, tz: tz() });
      setMessages((m) => [...m.map((x) => (x.message_id === msg.message_id ? { ...x, decision: "skipped" } : x)), data.ask]);
      setSkipFor(msg.message_id);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const submitSkipReason = async (messageId, reason) => {
    setSending(true);
    setSkipFor(null);
    setMessages((m) => [...m, { message_id: `tmp_${Date.now()}`, role: "user", content: reason, created_at: new Date().toISOString() }]);
    try {
      const { data } = await api.post(`/broker/sessions/${sessionId}/skip`, { message_id: messageId, reason, tz: tz() });
      setMessages((m) => [...m.filter((x) => !x.message_id.startsWith("tmp_")), data.user_message, data.reply]);
      toast.success("Got it — your broker will remember this");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setMessages((m) => m.filter((x) => !x.message_id.startsWith("tmp_")));
    } finally { setSending(false); }
  };

  return (
    <div className={`flex flex-col ${className}`} data-testid="broker-chat">
      <div className="flex-1 overflow-y-auto space-y-4 pr-1" data-testid="broker-messages">
        {messages.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-10">
            <Bot className="w-8 h-8 mx-auto mb-2 opacity-50" />
            Ask your broker anything — in English, தமிழ், हिंदी or any language. e.g. "I want to buy AAPL", "TSLA வாங்குனா என்னாகும்?"
          </div>
        )}
        {messages.map((m) => <Message key={m.message_id} m={m} onConfirm={confirm} onSkip={skip} onSpeak={speak} onStop={stopSpeaking} speaking={speakingId === m.message_id} onTradeAction={tradeAction} />)}
        {(sending || transcribing) && (
          <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="broker-typing">
            <Loader2 className="w-4 h-4 animate-spin text-emerald-400" /> {transcribing ? "Transcribing your voice…" : "Broker is checking live data…"}
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="mt-3 flex gap-2 items-end">
        <button type="button" onClick={toggleVoice} title={voiceOn ? "Voice replies on" : "Voice replies off"} aria-label="Toggle voice replies" data-testid="broker-voice-toggle"
          className={`h-10 w-10 shrink-0 rounded-xl border flex items-center justify-center transition-colors ${voiceOn ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10" : "border-white/10 text-slate-500 hover:text-slate-300"}`}>
          {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        <button type="button" onClick={recording ? stopRecording : startRecording} disabled={sending || transcribing} aria-label={recording ? "Stop recording" : "Speak to broker"} data-testid="broker-mic-button"
          className={`h-10 w-10 shrink-0 rounded-xl border flex items-center justify-center transition-colors ${recording ? "border-red-500 text-red-400 bg-red-500/10 animate-pulse" : "border-white/10 text-slate-300 hover:text-emerald-400"}`}>
          {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={1}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={recording ? "Listening… tap ■ when done" : skipFor ? "Tell your broker why you skipped…" : "Type or speak in any language — English, தமிழ், हिंदी…"}
          className="flex-1 resize-none rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60 max-h-32"
          data-testid="broker-input" />
        <Button type="submit" disabled={sending || !text.trim()} className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E] h-10 w-10 p-0" data-testid="broker-send-button" aria-label="Send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>
    </div>
  );
};

function Message({ m, onConfirm, onSkip, onSpeak, onStop, speaking, onTradeAction }) {
  if (m.role === "event") {
    return (
      <div className="flex justify-center" data-testid={`broker-event-${m.message_id}`}>
        <span className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> {m.content}</span>
      </div>
    );
  }
  const isUser = m.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`broker-msg-${m.role}`}>
      <div className={`max-w-[92%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${isUser ? "bg-emerald-500 text-[#07090E] rounded-br-md" : "bg-white/5 border border-white/10 text-slate-200 rounded-bl-md"}`}>
        {!isUser && (
          <div className="flex items-center justify-between gap-3 mb-1">
            <p className="text-[10px] uppercase tracking-wider text-emerald-400 flex items-center gap-1"><Bot className="w-3 h-3" /> Broker{m.followup && " · follow-up"}</p>
            <button type="button" onClick={() => (speaking ? onStop() : onSpeak(m))} aria-label={speaking ? "Stop" : "Play reply"} data-testid={`broker-play-${m.message_id}`}
              className={`text-slate-500 hover:text-emerald-400 transition-colors ${speaking ? "text-emerald-400" : ""}`}>
              {speaking ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
        <div className="prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-strong:text-white">
          <ReactMarkdown>{m.content}</ReactMarkdown>
        </div>
        {m.recommendation && <RecommendationCard rec={m.recommendation} m={m} onConfirm={onConfirm} onSkip={onSkip} />}
        {m.followup?.trade_id && <FollowupActions m={m} onAction={onTradeAction} />}
      </div>
    </div>
  );
}

function RecommendationCard({ rec, m, onConfirm, onSkip }) {
  const tradeable = rec.side === "BUY" || rec.side === "SELL";
  const color = rec.side === "BUY" ? "text-emerald-400" : rec.side === "SELL" ? "text-red-400" : "text-amber-400";
  return (
    <div className="mt-3 rounded-xl bg-[#0D111A] border border-white/10 p-3 space-y-2" data-testid={`broker-rec-${m.message_id}`}>
      <div className="flex items-center justify-between">
        <span className={`font-display font-bold ${color}`}>{rec.side} {rec.symbol}</span>
        <span className="font-mono-num text-xs"><span className="text-emerald-400">{rec.win_prob}% win</span> · <span className="text-red-400">{rec.loss_prob}% loss</span></span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center font-mono-num text-xs">
        <Cell label="Entry" v={rec.entry} /><Cell label="Stop" v={rec.stop_loss} c="text-red-400" /><Cell label="Target" v={rec.target} c="text-emerald-400" />
      </div>
      <div className="text-xs text-slate-300 space-y-1">
        {rec.entry_window && <p className="flex gap-1.5"><Clock className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" /><span><span className="text-slate-500">Entry window:</span> {rec.entry_window}</span></p>}
        {rec.exit_by && <p className="flex gap-1.5"><Target className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" /><span><span className="text-slate-500">Exit by:</span> {rec.exit_by}{rec.hold_duration ? ` · hold ${rec.hold_duration}` : ""}</span></p>}
        {rec.alternative?.symbol && <p className="flex gap-1.5"><ArrowRightLeft className="w-3.5 h-3.5 text-violet-400 shrink-0 mt-0.5" /><span><span className="text-slate-500">Better alternative:</span> {rec.alternative.symbol} @ {rec.alternative.entry} → {rec.alternative.target} ({rec.alternative.win_prob}%) — {rec.alternative.why}</span></p>}
        {rec.risks?.length > 0 && <p className="flex gap-1.5"><ShieldAlert className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" /><span><span className="text-slate-500">Risks:</span> {rec.risks.join(" · ")}</span></p>}
      </div>
      {tradeable && m.ask_confirmation !== false && !m.decision && (
        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={() => onConfirm(m)} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid={`broker-confirm-${m.message_id}`}>
            <CheckCircle2 className="w-4 h-4 mr-1.5" /> I'll Trade This
          </Button>
          <Button size="sm" variant="outline" onClick={() => onSkip(m)} className="flex-1 border-white/15 text-slate-300 hover:text-red-400 hover:border-red-500/40" data-testid={`broker-skip-${m.message_id}`}>
            <XCircle className="w-4 h-4 mr-1.5" /> Skip This Signal
          </Button>
        </div>
      )}
      {m.decision === "traded" && <p className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Logged in journal</p>}
      {m.decision === "skipped" && <p className="text-xs text-slate-500 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Skipped{m.skip_reason ? ` — ${m.skip_reason}` : ""}</p>}
    </div>
  );
}

function FollowupActions({ m, onAction }) {
  const [trailing, setTrailing] = useState(false);
  const [stop, setStop] = useState("");
  const f = m.followup;
  if (f.actioned) return <p className="mt-2 text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {f.actioned === "exit" ? "Exited" : "Stop trailed"}</p>;
  return (
    <div className="mt-3 rounded-xl bg-[#0D111A] border border-white/10 p-3 space-y-2" data-testid={`broker-followup-${m.message_id}`}>
      <p className="text-xs text-slate-400 font-mono-num">{f.type === "target" ? "Target" : "Stop"} hit at {f.price} · P&L {f.pnl_per_share >= 0 ? "+" : ""}{f.pnl_per_share}/share ({f.pnl_pct >= 0 ? "+" : ""}{f.pnl_pct}%)</p>
      {!trailing ? (
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onAction(m, "exit")} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid={`broker-exit-${m.message_id}`}>
            <LogOut className="w-4 h-4 mr-1.5" /> Exit now
          </Button>
          {f.type === "target" && (
            <Button size="sm" variant="outline" onClick={() => setTrailing(true)} className="flex-1 border-white/15 text-slate-300 hover:text-cyan-400" data-testid={`broker-trail-${m.message_id}`}>
              <TrendingUp className="w-4 h-4 mr-1.5" /> Trail stop
            </Button>
          )}
        </div>
      ) : (
        <div className="flex gap-2 items-center">
          <input type="number" step="any" value={stop} onChange={(e) => setStop(e.target.value)} placeholder="New stop (blank = auto)" data-testid={`broker-trail-input-${m.message_id}`}
            className="flex-1 rounded-lg bg-white/5 border border-white/10 px-2 py-1.5 text-xs font-mono-num focus:outline-none focus:border-cyan-500/60" />
          <Button size="sm" onClick={() => onAction(m, "trail", stop === "" ? null : Number(stop))} className="bg-cyan-500 hover:bg-cyan-400 text-[#07090E] font-semibold" data-testid={`broker-trail-confirm-${m.message_id}`}>Set</Button>
          <button type="button" onClick={() => setTrailing(false)} className="text-slate-500 hover:text-slate-300" aria-label="Cancel"><X className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

function WatchlistEditor({ symbols, isCustom, onSaved }) {
  const [list, setList] = useState(symbols);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => setList(symbols), [symbols]);
  const add = () => {
    const v = input.trim().toUpperCase();
    if (!v) return;
    if (list.length >= 15) { toast.error("Maximum 15 symbols"); return; }
    if (!list.includes(v)) setList([...list, v]);
    setInput("");
  };
  const save = async (next) => {
    setSaving(true);
    try { const { data } = await api.put("/broker/watchlist", { symbols: next }); setList(data.symbols); onSaved?.(data); toast.success(next.length ? "Watchlist saved — scanner will use it" : "Reset to default watchlist"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-3 rounded-xl bg-white/[0.03] border border-white/10 p-3" data-testid="watchlist-editor">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {list.map((s) => (
          <span key={s} className="text-[11px] font-mono-num bg-white/5 border border-white/10 rounded-full pl-2 pr-1 py-0.5 flex items-center gap-1" data-testid={`watchlist-chip-${s}`}>
            {s}<button type="button" onClick={() => setList(list.filter((x) => x !== s))} className="text-slate-500 hover:text-red-400" aria-label={`Remove ${s}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Add symbol e.g. NFLX, BTC, NIFTY" data-testid="watchlist-input"
          className="flex-1 rounded-lg bg-white/5 border border-white/10 px-2 py-1.5 text-xs font-mono-num focus:outline-none focus:border-emerald-500/60" />
        <button type="button" onClick={add} className="h-8 w-8 rounded-lg border border-white/10 text-slate-300 hover:text-emerald-400 flex items-center justify-center" aria-label="Add symbol" data-testid="watchlist-add"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="flex gap-2 mt-2">
        <Button size="sm" onClick={() => save(list)} disabled={saving || list.length === 0} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold h-8" data-testid="watchlist-save">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save watchlist"}</Button>
        {isCustom && <Button size="sm" variant="outline" onClick={() => save([])} disabled={saving} className="border-white/15 text-slate-300 h-8" data-testid="watchlist-reset">Reset</Button>}
      </div>
      <p className="text-[10px] text-slate-500 mt-1.5">{list.length}/15 symbols · scanned every 30 min</p>
    </div>
  );
}

const Cell = ({ label, v, c = "text-white" }) => (
  <div className="rounded-lg bg-white/5 py-1.5"><p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p><p className={`font-semibold ${c}`}>{v ?? "—"}</p></div>
);

export const ScannerPanel = () => {
  const [scan, setScan] = useState(null);
  const [running, setRunning] = useState(false);
  const [editing, setEditing] = useState(false);
  const load = () => api.get("/broker/scanner").then(({ data }) => setScan(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setRunning(true);
    try { const { data } = await api.post("/broker/scanner/run"); setScan(data); toast.success("Market scan complete"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };
  return (
    <div className="glass rounded-2xl p-5" data-testid="scanner-panel">
      <div className="flex items-center justify-between mb-1">
        <p className="text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> Today's Top Opportunities</p>
        <button onClick={run} disabled={running} className="text-xs text-slate-400 hover:text-emerald-400 flex items-center gap-1" data-testid="scanner-run-button">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Scan now
        </button>
      </div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] text-slate-500">{scan?.is_custom ? "Your watchlist" : "Default watchlist"} · every 30 min{scan?.generated_at ? ` · last ${new Date(scan.generated_at).toLocaleTimeString()}` : ""}</p>
        <button type="button" onClick={() => setEditing((e) => !e)} className="text-[11px] text-slate-400 hover:text-emerald-400 flex items-center gap-1" data-testid="watchlist-edit-toggle"><Pencil className="w-3 h-3" /> {editing ? "Done" : "Edit watchlist"}</button>
      </div>
      {editing && scan && <WatchlistEditor symbols={scan.user_watchlist || []} isCustom={scan.is_custom} onSaved={() => { load(); }} />}
      {scan?.is_stale && <p className="text-[11px] text-amber-400 mb-2" data-testid="scanner-stale">Showing default scan — tap "Scan now" for your watchlist.</p>}
      {scan?.market_note && <p className="text-xs text-slate-400 mb-3 italic">{scan.market_note}</p>}
      <ol className="space-y-2">
        {(scan?.opportunities || []).map((o, i) => (
          <li key={i} className="rounded-xl bg-white/[0.03] border border-white/10 p-3 text-sm" data-testid={`scanner-opp-${i}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono-num font-semibold">{i + 1}. {o.symbol} <span className={o.side === "BUY" ? "text-emerald-400" : "text-red-400"}>{o.side}</span></span>
              <span className="text-xs text-emerald-400 font-mono-num">{o.win_prob}% win</span>
            </div>
            <p className="text-xs text-slate-300 mt-1 font-mono-num">Entry {o.entry} → Target {o.target} · Stop {o.stop_loss}</p>
            <p className="text-xs text-slate-500">Hold {o.hold_duration} · valid {o.valid_for}</p>
          </li>
        ))}
        {scan && scan.opportunities?.length === 0 && <li className="text-xs text-slate-500">No scan yet — tap "Scan now".</li>}
      </ol>
    </div>
  );
};
